import InlineTextInput from '@/components/ui/inline-text-input';
import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { useCallback, useEffect, useState } from 'react';

const VehicleMileageEditor = ({
  initialMileage,
  dealId,
}: {
  initialMileage: number;
  dealId: Deal['id'];
}) => {
  const queryClient = useQueryClient();

  const dealShortId = dealStore.getById(dealId)?.short_id;
  const [mileage, setMileage] = useState(initialMileage?.toString() || '');

  const updateMileageMutation = useMutation({
    onMutate: async ({ mileage }: { mileage: number }) => {
      dealStore.updateVehicleDeliveryOdometer({
        id: dealId,
        vehicleDeliveryOdometer: mileage,
      });
    },
    mutationFn: async ({ mileage }: { mileage: number }) => {
      const response = await pdkAxios.post(
        `/deals/${dealId}/vehicle-delivery-odometer`,
        {
          vehicle_delivery_odometer: mileage,
        },
      );

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating mileage', error, variables);
      // TODO: handle error by adding the original cdkId back to the store
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handleMileageChange = useCallback(
    debounce((newMileage: number) => {
      updateMileageMutation.mutate({
        mileage: newMileage,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (mileage === (initialMileage?.toString() || '')) return;

    const parsedMileage = parseInt(mileage);

    if (isNaN(parsedMileage)) return;

    handleMileageChange(parsedMileage);
  }, [mileage]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) return;
    if (Number(e.target.value) == 0) {
      setMileage('');
    } else {
      setMileage(e.target.value);
    }
  }, []);

  useEffect(() => {
    setMileage(initialMileage?.toString() || '');
  }, [initialMileage]);

  return (
    <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
      <div className="text-sm text-black dark:text-white">
        Delivery Odometer
      </div>
      <InlineTextInput
        value={mileage?.toString()}
        onChange={onChange}
        placeholder="Enter delivery odometer"
        className="text-right"
        wrapperClassName="gap-1"
      />
      <ConfirmationDot
        targetId={dealId}
        targetType={ConfirmationTargetType.Deal}
        field={ConfirmationField.Odometer}
        value={observable.box(mileage?.toString() || '')}
      />
    </div>
  );
};

export default VehicleMileageEditor;
